import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { toast } from 'sonner';
import {
  Plus,
  MagnifyingGlass as Search,
  CalendarBlank as CalendarIcon,
  MapPin,
  Gavel,
  Users,
  CheckCircle,
  WarningCircle,
  ArrowRight,
  CalendarX,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RowActions } from '@/components/shared/RowActions';
import { StatCard } from '@/components/shared/StatCard';
import { useHearingsStore, Hearing } from '@/lib/hearingsStore';
import { searchHearings } from '@/lib/hearingsApi';
import { useServerSearch } from '@/lib/useServerSearch';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import {
  LEGAL_OUTCOME_TYPES,
  OUTCOME_NOTES_MAX,
  OUTCOME_SUMMARY_MIN,
  outcomeSummaryError,
  outcomeTypeErrorFor,
} from '@/lib/hearingConstants';
import { isMeaningfulTitle } from '@/lib/validators';
import { ScrollLock } from '@/components/shared/ScrollLock';

/** Format ISO date string (e.g. '2026-04-10') to display label (e.g. 'Thu 10 Apr') */
function fmtDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

function daysUntil(isoDate: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate + 'T00:00:00'); target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000);
}

const TIME_FILTERS = [
  { label: 'Upcoming',   match: 'upcoming' },
  { label: 'Today',      match: 'today' },
  { label: 'This week',  match: 'this_week' },
  { label: 'Next week',  match: 'next_week' },
  { label: 'This month', match: 'this_month' },
  { label: 'Past',       match: 'past' },
];

const TYPE_FILTERS = [
  { label: 'All types',    match: '' },
  { label: 'Trial',        match: 'Trial' },
  { label: 'CMC',          match: 'CMC' },
  { label: 'PTR',          match: 'PTR' },
  { label: 'Application',  match: 'Application' },
  { label: 'Appeal',       match: 'Appeal' },
  { label: 'Sentencing',   match: 'Sentencing' },
];

const PAGE_SIZE = 10;

// ─── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    scheduled:             'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    confirmed:             'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    in_progress:           'bg-brand-100 text-brand-700 dark:text-brand-300',
    tentative:             'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    adjourned:             'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    awaiting_appearance:   'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    awaiting_compliance:   'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
    order_reserved:        'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
    judgment_pending:      'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
    completed:             'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    vacated:               'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
    no_show:               'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
    disposed:              'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
  };
  const labels: Record<string, string> = {
    scheduled:             'Scheduled',
    confirmed:             'Confirmed',
    in_progress:           'In Progress',
    tentative:             'Tentative',
    adjourned:             'Adjourned',
    awaiting_appearance:   'Awaiting Appearance',
    awaiting_compliance:   'Awaiting Compliance',
    order_reserved:        'Order Reserved',
    judgment_pending:      'Judgment Pending',
    completed:             'Completed',
    vacated:               'Vacated',
    no_show:               'No Show',
    disposed:              'Disposed',
  };
  const cls = variants[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300';
  const label = labels[status] ?? status;
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

// ─── Next action helper ────────────────────────────────────────────────────────

function getNextAction(h: Hearing): { label: string; urgent: boolean } | null {
  if (['completed', 'vacated', 'disposed'].includes(h.status)) return null;
  const days = daysUntil(h.date);
  if (h.status === 'adjourned' && !h.nextHearingDate) return { label: 'Schedule next hearing', urgent: true };
  if (h.status === 'order_reserved' || h.status === 'judgment_pending') return { label: 'Await judgment', urgent: false };
  if (h.status === 'awaiting_compliance') return { label: 'Chase compliance', urgent: true };
  if (days < 0 && !h.outcome) return { label: 'Record outcome', urgent: true };
  if (days === 0) return { label: 'In court today', urgent: true };
  if (days <= 2) return { label: 'Prepare hearing brief', urgent: true };
  return { label: 'Upcoming', urgent: false };
}

// ─── Individual hearing card ───────────────────────────────────────────────────

function HearingCard({
  h,
  basePath,
  onRecordOutcome,
  onAdjourn,
  onViewDetail,
}: {
  h: Hearing;
  basePath: string;
  onRecordOutcome: (h: Hearing) => void;
  onAdjourn: (h: Hearing) => void;
  onViewDetail: (h: Hearing) => void;
}) {
  const navigate = useNavigate();
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const days = daysUntil(h.date);

  const countdownCls =
    days <= 0
      ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'
      : days <= 2
      ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'
      : days <= 7
      ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300'
      : 'bg-muted text-muted-foreground';

  const countdownText =
    days === 0
      ? 'Today'
      : days < 0
      ? `${Math.abs(days)}d ago`
      : days === 1
      ? 'Tomorrow'
      : `In ${days}d`;

  const terminalStatuses = ['completed', 'vacated', 'disposed', 'adjourned'];
  const isUrgent = days >= 0 && days <= 2 && !terminalStatuses.includes(h.status);
  const hasOutcome = !!h.outcome;
  const isActive = !terminalStatuses.includes(h.status);
  const nextAction = getNextAction(h);

  return (
    <div
      className={`group rounded-xl border bg-card p-4 shadow-sm transition hover:border-brand-200/60 hover:shadow-md ${
        isUrgent ? 'border-amber-200 dark:border-amber-500/30 ring-1 ring-amber-100' : 'border-border'
      }`}
    >
      {/* Top row: date · time · countdown · type · status */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-foreground">{fmtDate(h.date)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-sm text-muted-foreground">{h.time}</span>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${countdownCls}`}>
          {countdownText}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {h.type && (
            <span className="rounded-full border border-border bg-muted/60 px-2.5 py-0.5 text-xs font-medium text-foreground">
              {h.type}
            </span>
          )}
          <StatusBadge status={h.status} />
        </div>
      </div>

      {/* Matter + court info */}
      <div className="mb-3">
        <button
          className="text-left font-semibold text-foreground leading-snug hover:text-brand-600 dark:hover:text-brand-400 hover:underline transition"
          onClick={() => { if (h.matterId) navigate(`${basePath}/matters/${h.matterId}`); }}
        >
          {h.matterName}
        </button>
        {h.matterNumber && (
          <div className="text-xs text-muted-foreground mt-0.5">{h.matterNumber}</div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span>{h.court}{h.courtroom ? ` · Room ${h.courtroom}` : ''}</span>
          {h.judge && (
            <>
              <span>·</span>
              <Gavel className="h-3.5 w-3.5 shrink-0" />
              <span>{h.judge}</span>
            </>
          )}
          {h.isVirtual && (
            <span className="ml-1 rounded-full bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300">
              Virtual
            </span>
          )}
        </div>
      </div>

      {/* Team row + next action chip */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-muted-foreground">
        {h.leadBarrister && (
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            <span>{h.leadBarrister}</span>
            {h.juniorBarrister && <span className="ml-0.5 text-muted-foreground/60">+ {h.juniorBarrister}</span>}
          </span>
        )}
        {h.durationLabel && (
          <span className="text-muted-foreground/70">{h.durationLabel}</span>
        )}
        {nextAction && (
          <span
            className={`ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
              nextAction.urgent
                ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            <ArrowRight className="h-3 w-3" />
            {nextAction.label}
          </span>
        )}
      </div>

      {/* Outcome strip */}
      {hasOutcome && (
        <div className="mb-3 rounded-lg border border-emerald-200/60 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300">
          <span className="font-semibold">Outcome: </span>{h.outcome}
        </div>
      )}

      {/* Action row */}
      {/* Wraps: the row carries up to three buttons and then the view/edit/cancel menu pushed to
          the right, which on a phone came to more than the card is wide — the menu was 4px past
          the edge with the buttons it was sharing the line with, and a hearing offering both
          "Record outcome" and "Adjourn" puts it further out still. `ml-auto` keeps the menu to
          the right of whichever line it ends up on. */}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50">
        {isActive && !hasOutcome && (
          <Button
            variant="outline"
            size="xs"
            leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
            onClick={() => onRecordOutcome(h)}
          >
            Record outcome
          </Button>
        )}
        {isActive && (
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<WarningCircle className="h-3.5 w-3.5" />}
            // Opens the hearing's adjourn form (reason + next date). This used to save a status-only
            // "adjourned" and then navigate — which lost both and hid the form's own Adjourn button.
            onClick={() => onAdjourn(h)}
          >
            Adjourn
          </Button>
        )}
        <div className="ml-auto">
          <RowActions
            onView={() => onViewDetail(h)}
            onEdit={() => navigate(`${basePath}/hearings/${h.id}`)}
            onDelete={() => {
              void updateHearing(h.id, { status: 'vacated' })
                .then((saved) => { if (saved) toast.success(`${h.matterName} — hearing cancelled`); });
            }}
            viewLabel="View"
            editLabel="Edit"
            deleteLabel="Cancel hearing"
            // A bin reads as "erase this record". This action vacates the listing and keeps it on
            // the matter for good, so it carries the non-destructive icon the component takes for
            // exactly this case (RowActions.tsx:44).
            deleteIcon={<CalendarX className="h-4 w-4" />}
            disableDelete={!isActive}
            deleteConfirm={{
              title: 'Cancel hearing?',
              message: `Cancel the hearing for “${h.matterName}”? This clears reminders and updates the diary.`,
              confirmLabel: 'Cancel hearing',
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function HearingsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';

  const allHearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const updateHearing = useHearingsStore((s) => s.updateHearing);
  const wsConnected = useWebSocketStore((s) => s.connected);

  useEffect(() => { fetchHearings(); }, [fetchHearings]);
  useEffect(() => {
    const unsub = onCalendarUpdate(() => { fetchHearings(); });
    return unsub;
  }, [wsConnected, fetchHearings]);

  const [search, setSearch] = useState('');
  // Default to Upcoming — past hearings are hidden until the user explicitly picks "Past".
  const [timeFilter, setTimeFilter] = useState('upcoming');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [outcomeModal, setOutcomeModal] = useState<{ id: string; name: string } | null>(null);
  const [outcomeText, setOutcomeText] = useState('');
  const [outcomeType, setOutcomeType] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);
  // Shown only after a save attempt, so the modal doesn't open shouting at the user.
  const [outcomeSubmitted, setOutcomeSubmitted] = useState(false);
  const outcomeTypeError = outcomeTypeErrorFor(outcomeType, outcomeSubmitted);
  const outcomeTextError = outcomeSummaryError(outcomeText, isMeaningfulTitle, outcomeSubmitted);

  // Server-side search finds hearings beyond the client-loaded window (older/others'
  // accessible hearings). When active it replaces the in-memory list and ignores the
  // time filter, so searching never hides a matching past hearing. Canonical hook (H3).
  const { isSearching, results: serverResults } = useServerSearch<Hearing>(search, {
    key: ['hearings-search'],
    fetcher: (q) => searchHearings(q),
  });

  const filtered = useMemo(() => {
    let data = isSearching ? [...serverResults] : [...allHearings];

    if (timeFilter && !isSearching) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() + mondayOffset);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      const nextWeekStart = new Date(weekStart);
      nextWeekStart.setDate(weekStart.getDate() + 7);
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      data = data.filter((h) => {
        const d = new Date(h.date + 'T00:00:00');
        if (timeFilter === 'upcoming')   return d.getTime() >= today.getTime(); // today onward
        if (timeFilter === 'past')       return d.getTime() < today.getTime();  // before today
        if (timeFilter === 'today')      return d.getTime() === today.getTime();
        if (timeFilter === 'this_week')  return d >= weekStart && d <= weekEnd;
        if (timeFilter === 'next_week')  return d >= nextWeekStart && d <= nextWeekEnd;
        if (timeFilter === 'this_month') return d >= monthStart && d <= monthEnd;
        return true;
      });
    }

    if (typeFilter) {
      data = data.filter((h) => h.type === typeFilter);
    }

    // Idle-path client filter only — when isSearching, the server already matched.
    if (search.trim() && !isSearching) {
      const q = search.toLowerCase();
      data = data.filter(
        (h) =>
          h.matterName.toLowerCase().includes(q) ||
          h.court.toLowerCase().includes(q) ||
          h.judge.toLowerCase().includes(q) ||
          h.leadBarrister.toLowerCase().includes(q) ||
          h.juniorBarrister.toLowerCase().includes(q),
      );
    }

    // Order: UPCOMING hearings first (soonest at the very top), then PAST hearings
    // (most-recent first). A flat descending sort put the furthest-future date on top,
    // burying today's/next hearings under distant ones — this keeps the next hearing at
    // the top and recent history right below it.
    const todayKey = new Date().toISOString().slice(0, 10);
    data.sort((a, b) => {
      const aUpcoming = a.date >= todayKey;
      const bUpcoming = b.date >= todayKey;
      if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1; // upcoming block first
      return aUpcoming
        ? a.date.localeCompare(b.date) || a.time.localeCompare(b.time)  // upcoming: soonest first
        : b.date.localeCompare(a.date) || b.time.localeCompare(a.time); // past: most recent first
    });
    return data;
  }, [allHearings, serverResults, isSearching, search, timeFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Stats
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekEnd = new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10);
  const thisWeekCount = allHearings.filter((h) => h.date >= todayStr && h.date <= weekEnd && h.status !== 'vacated').length;
  const todayCount    = allHearings.filter((h) => h.date === todayStr && h.status !== 'vacated').length;
  const outcomesDue   = allHearings.filter((h) => h.date < todayStr && h.status !== 'completed' && h.status !== 'vacated').length;
  const adjournments  = allHearings.filter((h) => h.status === 'adjourned').length;

  return (
    <>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Hearings"
        subtitle="Schedule, track and record outcomes for every hearing"
        // Three actions wrap onto their own row below ~1250px. Without this they huddle at its
        // right edge with a long empty run beside them; with it they share the row they already own.
        actionsFill
        actions={
          <>
            <Button
              variant="outline"
              size="md"
              leftIcon={<CalendarIcon className="h-4 w-4" />}
              onClick={() => navigate(basePath === '/clerk' ? '/clerk/diary' : '/admin/calendar')}
            >
              Calendar view
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate(`${basePath}/hearings/new?mode=record`)}
            >
              Record past hearing
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(`${basePath}/hearings/new`)}
            >
              Schedule hearing
            </Button>
          </>
        }
      />

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard label="This week"    value={thisWeekCount} hint="next 7 days" />
        <StatCard label="Today"        value={todayCount}    hint={todayCount > 0 ? 'court today' : 'none listed'} />
        <StatCard label="Outcomes due" value={outcomesDue}   hint={outcomesDue > 0 ? 'awaiting result entry' : 'all recorded'} />
        <StatCard label="Adjourned"    value={adjournments}  hint="pending relist" />
      </div>

      {/* Search + filter bar */}
      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Filter by matter, court, judge or barrister…"
              className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-0.5 text-xs sm:w-full xl:w-auto">
            {TIME_FILTERS.map((s) => (
              <button
                key={s.label}
                onClick={() => { setTimeFilter(s.match); setPage(1); }}
                className={`whitespace-nowrap rounded-sm px-3 py-1.5 font-medium transition sm:flex-1 sm:px-2 sm:text-center xl:flex-none xl:px-3 ${
                  timeFilter === s.match ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1 pt-2 border-t border-border/50">
          {TYPE_FILTERS.map((s) => (
            <button
              key={s.label}
              onClick={() => { setTypeFilter(s.match); setPage(1); }}
              className={`rounded-md border px-3 py-1 text-xs font-medium transition ${
                typeFilter === s.match
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-border bg-card text-muted-foreground hover:border-brand-300 hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Card list */}
      <div className="space-y-3">
        {paginated.filter((h) => !h.id.startsWith('temp-')).map((h) => (
          <HearingCard
            key={h.id}
            h={h}
            basePath={basePath}
            onViewDetail={(hearing) => navigate(`${basePath}/hearings/${hearing.id}`)}
            onRecordOutcome={(hearing) => {
              setOutcomeModal({ id: hearing.id, name: hearing.matterName });
              setOutcomeText('');
              setOutcomeType('');
              setOutcomeSubmitted(false);
            }}
            onAdjourn={(hearing) => navigate(`${basePath}/hearings/${hearing.id}?adjourn=1`)}
          />
        ))}

        {paginated.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-dashed border-border bg-card">
            <CalendarIcon className="mb-3 h-10 w-10 text-muted-foreground/30" weight="duotone" />
            <p className="text-sm font-semibold text-foreground">No hearings match your filters</p>
            <p className="mt-1 text-xs text-muted-foreground">Try changing the date range or clearing the search.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3 text-xs text-muted-foreground">
          <div>
            Showing{' '}
            <span className="text-foreground">
              {paginated.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)}
            </span>{' '}
            of <span className="text-foreground">{filtered.length}</span> hearings
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="xs"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              Previous
            </Button>
            <span className="px-2 text-xs text-foreground">{safePage} / {totalPages}</span>
            <Button
              variant="outline"
              size="xs"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Quick record outcome modal */}
      {outcomeModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-xl border border-border bg-card shadow-2xl">
            <div className="border-b border-border px-5 py-4">
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Record outcome
              </div>
              <div className="mt-0.5 text-base font-semibold text-foreground">{outcomeModal.name}</div>
            </div>
            <div className="px-5 py-4">
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Outcome type <span className="text-red-500">*</span>
              </label>
              <select
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 ${outcomeTypeError ? 'border-red-400 focus:ring-red-500/40' : 'border-border focus:ring-brand-500'}`}
                value={outcomeType}
                onChange={(e) => setOutcomeType(e.target.value)}
              >
                <option value="">Select outcome…</option>
                {LEGAL_OUTCOME_TYPES.filter((o) => o.value !== 'adjourned' && o.value !== 'trial_continued').map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className={`mb-3 mt-1 text-xs ${outcomeTypeError ? 'text-red-600 dark:text-red-400' : 'text-transparent'}`}>
                {outcomeTypeError || '.'}
              </p>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Outcome notes <span className="text-red-500">*</span>
              </label>
              <textarea
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${outcomeTextError ? 'border-red-400 focus:ring-red-500/40' : 'border-border focus:ring-brand-500'}`}
                rows={4}
                maxLength={OUTCOME_NOTES_MAX}
                placeholder="e.g. Judgment reserved. Next directions hearing listed for 14 August."
                value={outcomeText}
                onChange={(e) => setOutcomeText(e.target.value)}
                autoFocus
              />
              {outcomeTextError
                ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{outcomeTextError}</p>
                : <p className="mt-1 text-xs text-muted-foreground">At least {OUTCOME_SUMMARY_MIN} characters — this is what the matter record will show.</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOutcomeModal(null)}
                disabled={savingOutcome}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={savingOutcome}
                onClick={async () => {
                  setOutcomeSubmitted(true);
                  if (outcomeTypeError || outcomeTextError || !outcomeType || !outcomeText.trim()) {
                    toast.error('Please complete the highlighted fields.');
                    return;
                  }
                  setSavingOutcome(true);
                  try {
                    // Keep the modal (and the typed outcome) open if the server refuses it.
                    if (!(await updateHearing(outcomeModal.id, { outcome: outcomeText.trim(), outcomeType, status: 'completed' }))) return;
                    toast.success('Outcome recorded');
                    setOutcomeModal(null);
                  } finally {
                    setSavingOutcome(false);
                  }
                }}
              >
                {savingOutcome ? 'Saving…' : 'Save outcome'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
